import { makeStyles } from '@material-ui/core'
import { Background, ConnectionMode, ReactFlow } from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import PropTypes from 'prop-types'
import React, { useCallback, useMemo, useRef } from 'react'

import { useEntityGraphContext } from '../Wrapper/EntityGraphProvider'
import { getGraphTypePlugin } from '../GraphTypes'
import FloatingEdge from './edges/FloatingEdge'
import ZoomAwareEdge from './edges/ZoomAwareEdge'
import { nodeTypes } from './nodes'

const useStyles = makeStyles({
  graphContainer: {
    '.react-flow__node': {
      '.parent': {
        background: 'var(--background-primary)'
      }
    }
  }
})

export const edgeTypes = {
  zoomAware: ZoomAwareEdge,
  default: FloatingEdge
}

const Chart = ({
  children,
  includeBackground = true,
  allowMove = true,
  styles,
  reactFlowProps = {}
}) => {
  const classes = useStyles()
  const { state, dispatch } = useEntityGraphContext()
  const nodes = state.data.nodes
  const edges = state.data.edges
  const nodesEditable = state.config.canvasSettings?.nodesEditable ?? true
  const graphType = state.graphType
  const plugin = useMemo(() => getGraphTypePlugin(graphType), [graphType])

  const chartNodeTypes = Object.entries(nodeTypes).reduce((acc, [key, value]) => {
    acc[key] = value.component
    return acc
  }, {})

  const pendingNodeChanges = useRef(null)
  const pendingEdgeChanges = useRef(null)
  const frameNode = useRef(null)
  const frameEdge = useRef(null)
  const onNodesChange = useCallback((changes) => {
    if (!nodesEditable) return
    pendingNodeChanges.current = changes
    if (frameNode.current) cancelAnimationFrame(frameNode.current)
    frameNode.current = requestAnimationFrame(() => {
      dispatch({ type: 'UPDATE_NODES', payload: pendingNodeChanges.current })
    })
  }, [nodesEditable, dispatch])

  const onEdgesChange = useCallback((changes) => {
    if (!nodesEditable) return
    pendingEdgeChanges.current = changes
    if (frameEdge.current) cancelAnimationFrame(frameEdge.current)
    frameEdge.current = requestAnimationFrame(() => {
      dispatch({ type: 'UPDATE_EDGES', payload: pendingEdgeChanges.current })
    })
  }, [nodesEditable, dispatch])

  const onConnect = useCallback((connection) => {
    if (!nodesEditable) return
    connection.sourceHandle = connection.sourceHandle.replace('target_', 'handle_')

    if (plugin?.onConnect) {
      connection = plugin.onConnect(connection, nodes)
    }

    dispatch({ type: 'ADD_EDGE', payload: connection })
  }, [nodesEditable, nodes, dispatch, plugin])

  const flowRef = useRef(null)
  const onInit = useCallback((instance) => { flowRef.current = instance }, [])

  const onDrop = useCallback(
    (event) => {
      event.preventDefault()
      const dataString = event.dataTransfer.getData('application/reactflow')
      const { nodeType, id, data } = JSON.parse(dataString)
      if (!nodeType || !flowRef) return

      const position = flowRef.current.screenToFlowPosition({ x: event.clientX, y: event.clientY })

      const newNode = {
        id,
        type: nodeType,
        position,
        data
      }

      dispatch({ type: 'ADD_NODE', payload: newNode })
      dispatch({ type: 'SET_SELECTED_NODE', payload: newNode })
    },
    [dispatch]
  )

  const onDragOver = useCallback((event) => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
  }, [])

  return (
    <div className={classes.graphContainer}>
      <div style={{ height: '90vh', width: '100%', border: '1px solid #ccc', ...styles }}>
        <ReactFlow
          connectionMode={ConnectionMode.Loose}
          edgeTypes={edgeTypes}
          edges={edges}
          fitView
          nodeTypes={chartNodeTypes}
          nodes={nodes}
          nodesDraggable={nodesEditable}
          onConnect={onConnect}
          onDragOver={onDragOver}
          onDrop={onDrop}
          onEdgesChange={onEdgesChange}
          onInit={onInit}
          onNodesChange={onNodesChange}
          proOptions={{ hideAttribution: true }}
          panOnDrag={allowMove}
          zoomOnScroll={allowMove}
          preventScrolling={!allowMove}
          style={{
            pointerEvents: allowMove ? 'auto' : 'none'
          }}
          {...reactFlowProps}
        >
          {includeBackground && <Background color='#E6E6E6' />}
          {children}
        </ReactFlow>
      </div>
    </div>
  )
}

Chart.propTypes = {
  children: PropTypes.node,
  allowMove: PropTypes.bool,
  includeBackground: PropTypes.bool,
  styles: PropTypes.object,
  reactFlowProps: PropTypes.object
}

export default Chart
